import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import firstFrameUrl from '@/assets/webp/loginIllustrationFirstFrame.webp?url';

// Lazy so lottie-web is not part of the auth chunk the form needs first
const LottieAnimation = lazy(() =>
  import('@shared/components/LottieAnimation').then((mod) => ({
    default: mod.LottieAnimation,
  }))
);

// Served from public/ and fetched at runtime so the ~1.4MB JSON is not
// bundled (and counted) as JavaScript. If this file changes, regenerate the
// first-frame placeholder with scripts/generate-lottie-first-frame.cjs.
const ANIMATION_URL = '/assets/lottie/illustrationBorder.json';

// Module-level cache shared by every auth page (login, forgot password, reset
// password, ...) so navigating between them reuses the parsed animation
// instead of fetching it again and flashing an empty panel
let cachedAnimationData: object | null = null;
let pendingRequest: Promise<object> | null = null;

function loadAnimationData(): Promise<object> {
  if (!pendingRequest) {
    pendingRequest = fetch(ANIMATION_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load animation: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        cachedAnimationData = data;
        return data;
      })
      .catch((error) => {
        // Drop the failed request so the next mount can retry
        pendingRequest = null;
        throw error;
      });
  }
  return pendingRequest;
}

interface LoginIllustrationProps {
  /**
   * CSS width of the square animation, resolved against the panel width
   * (e.g. 'min(90.25%, 91.2vh)'). Height follows from the 1:1 aspect ratio.
   */
  width: string;
  /** Sizing classes for the gradient panel. */
  className?: string;
}

/**
 * Login illustration panel shared by the desktop and tablet auth layouts:
 * the Figma white → blue vertical gradient with the looping Lottie centred.
 * A pre-rendered image of the animation's own first frame shows until the
 * Lottie has drawn, so the swap is seamless; later auth pages reuse the
 * already-loaded animation data.
 */
export function LoginIllustration({
  width,
  className = '',
}: LoginIllustrationProps) {
  const [animationData, setAnimationData] = useState<object | null>(
    () => cachedAnimationData
  );
  const [isAnimationDrawn, setIsAnimationDrawn] = useState(false);
  const handleAnimationDrawn = useCallback(() => setIsAnimationDrawn(true), []);

  useEffect(() => {
    if (cachedAnimationData) {
      return;
    }

    let cancelled = false;

    // Defer the first fetch until the browser is idle so it does not compete
    // with the form's own resources on first load
    const scheduleLoad =
      window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 500));

    scheduleLoad(() => {
      loadAnimationData()
        .then((data) => {
          if (!cancelled) {
            setAnimationData(data);
          }
        })
        .catch((error) => {
          console.error('Failed to load login illustration:', error);
        });
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      data-testid="login-illustration"
      aria-hidden
      className={`flex items-center justify-center bg-gradient-to-b from-white to-[#A7C2DE] ${className}`}
    >
      {/* Fixed square box so the panel does not shift when the animation lands */}
      <div className="relative" style={{ width, aspectRatio: '1 / 1' }}>
        {/* Same square as the Lottie, so it covers it exactly; positioned, so
            it paints over the Lottie until that has drawn */}
        {!isAnimationDrawn && (
          <img
            data-testid="login-illustration-placeholder"
            src={firstFrameUrl}
            alt=""
            className="absolute inset-0 h-full w-full"
          />
        )}
        {animationData && (
          <Suspense fallback={null}>
            <LottieAnimation
              animationData={animationData}
              loop
              width="100%"
              aspectRatio="1 / 1"
              onDOMLoaded={handleAnimationDrawn}
            />
          </Suspense>
        )}
      </div>
    </div>
  );
}
